import { Box, CircularProgress } from '@mui/material';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import styles from './BuilderLayout.module.css';
import FooterBar from 'pages/DocumentBuilder/components/FooterBar';
import NotesSideBar from 'pages/DocumentBuilder/components/NotesSideBar';
import ContentSidebar from 'pages/DocumentBuilder/components/ContentSidebar/ContentSidebar';
import SidebarButton from './components/SidebarButton/SidebarButton';
import DocumentBuilder from 'pages/DocumentBuilder';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useGetDocumentQuery } from 'features/document/documentApi';
import {
  DocumentType,
  resetState,
  selectSelectedItem,
  selectTemplateDocument,
  toggleSidebar,
  updateDocumentName,
  updateDocumentType,
  updateInitialDocument,
  updateNoteContent,
  updateNoteTitle,
  updateTemplateJson,
} from 'features/document/documentSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import NotFound from '../NotFound/NotFound';
import DocumentNavBar from '../Navbar/DocumentNavbar';
import { resetContract, resetQuote, selectIsSalesforceAuth } from 'features/salesforce/salesforceSlice';
import { SidebarSectionsEnum } from 'features/document/constants';
import { useGetTemplateQuery } from 'features/template/templateApi';
import VersionHistorySidebar from 'pages/DocumentBuilder/components/ContentSidebar/VersionHistorySidebar/VersionHistorySidebar';

const BuilderLayout: React.FC = () => {
  const { id, type } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useAppDispatch();
  const selector = useAppSelector;
  const navigate = useNavigate();
  const isSalesforceAuth = useAppSelector(selectIsSalesforceAuth);
  const defaultZoom = 80;
  const isCreate = pathname.includes('create');
  const isTemplate = pathname.includes('templates');
  const [isVersionHistorySidebarOpen, setIsVersionHistorySidebarOpen] = useState(false);

  let queryResult;

  if (!isTemplate) {
    queryResult = useGetDocumentQuery(Number(id), {
      skip: id === undefined,
      refetchOnMountOrArgChange: true,
    });
  } else {
    queryResult = useGetTemplateQuery(id, {
      skip: id === undefined,
      refetchOnMountOrArgChange: true,
    });
  }

  const { data, error, isLoading, isSuccess } = queryResult;

  const isThumbnailSidebarOpen = selector(state => state.document.isSidebarOpen);
  const templateJson = selector(state => selectTemplateDocument(state));
  const [sidebarSection, setSidebarSection] = useState(SidebarSectionsEnum.Blocks);
  const [currentPage, setCurrentPage] = useState(templateJson.pages.length > 0 ? 1 : 0);
  const [zoom, setZoom] = useState<number>(defaultZoom);
  const pageRefs = useRef(templateJson.pages.map(() => React.createRef<HTMLDivElement>()));
  const thumbnailPagesRef = useRef(templateJson.pages.map(() => React.createRef<HTMLDivElement>()));

  const selectedItem = useAppSelector(selectSelectedItem);

  useEffect(() => {
    if (selectedItem && sidebarSection !== SidebarSectionsEnum.TextEditor) {
      setSidebarSection(SidebarSectionsEnum.TextEditor);
    } else if (!selectedItem && sidebarSection == SidebarSectionsEnum.TextEditor) {
      setSidebarSection(SidebarSectionsEnum.Blocks);
    }
  }, [selectedItem]);

  const handleScroll = () => {
    const relativeLimit = 0.15 / (zoom / 100);

    for (let i = 0; i < templateJson.pages.length; i++) {
      const page = document.getElementById(templateJson.pages[i].id);
      if (page) {
        const pagePosition = page.getBoundingClientRect();
        const relativeTop = pagePosition.top / pagePosition.height;

        const shouldUpdatePage = relativeTop < relativeLimit && relativeTop > 0;

        if (shouldUpdatePage) {
          setCurrentPage(i + 1);
        }
      }
    }
  };

  useEffect(() => {
    pageRefs.current = templateJson.pages.map(() => React.createRef<HTMLDivElement>());
  }, [templateJson.pages]);

  const scrollToPage = (pageNumber: number, fromThumbnail: boolean) => {
    if (fromThumbnail) {
      const page = pageRefs.current[pageNumber];
      setCurrentPage(pageNumber + 1);
      if (page && page.current) {
        return page.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    if (!fromThumbnail) {
      const page = pageRefs.current[pageNumber - 1];
      setCurrentPage(pageNumber);
      if (page && page.current) {
        return page.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  useEffect(() => {
    if (id && data && isSuccess) {
      if (data.data.type !== DocumentType.DOCUMENT && !isSalesforceAuth) {
        navigate(isTemplate ? '/templates' : '/documents');
      }
      dispatch(updateTemplateJson({ templateJson: data.data.templateJson }));
      dispatch(updateInitialDocument({ document: data.data }));
      dispatch(updateNoteContent(data.data.noteContent));
      dispatch(updateDocumentName(data.data.name));
      dispatch(updateNoteTitle(data.data.noteTitle));
      dispatch(updateDocumentType(data?.data.type));
    }
    return () => {
      dispatch(resetState());
      dispatch(resetContract());
      dispatch(resetQuote());
    };
  }, [dispatch, id, data, isLoading]);

  useEffect(() => {
    if (isCreate) {
      dispatch(updateDocumentType(String(type).toUpperCase() as DocumentType));
    }
  }, [dispatch, isCreate]);

  const [areNotesOpen, setAreNotesOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleCloseNotes = () => {
    setAreNotesOpen(false);
  };
  const handleOpenNotes = () => {
    if (isThumbnailSidebarOpen) {
      dispatch(toggleSidebar());
    }
    setAreNotesOpen(!areNotesOpen);
  };
  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);

    if (isVersionHistorySidebarOpen) {
      setIsVersionHistorySidebarOpen(false);
    }
  };

  const handleClickVersionHistoryButton = () => {
    setIsVersionHistorySidebarOpen(!isVersionHistorySidebarOpen);
    if (isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  const renderLoader = () => (
    <Box className={styles.Layout}>
      <CircularProgress />
    </Box>
  );

  const renderNotFound = () => (
    <Box className={styles.Layout}>
      <NotFound />
    </Box>
  );

  const renderDocumentBuilder = () => (
    <Box className={styles.builderLayout}>
      <DocumentNavBar isCreate={!id} onClickVersionHistoryButton={handleClickVersionHistoryButton} />
      <Box className={styles.mainLayout}>
        {areNotesOpen && <NotesSideBar handleClose={handleCloseNotes} />}
        <Box className={styles.middleLayout}>
          <Box className={styles.builder} onScroll={handleScroll}>
            <div className={styles.toolbarText} id='myToolbarContainer' />
            <DocumentBuilder
              pages={templateJson.pages}
              pagesRef={pageRefs}
              zoom={zoom}
              thumbnailPagesRef={thumbnailPagesRef}
              scrollToPage={scrollToPage}
            />
          </Box>
          <FooterBar
            currentPage={currentPage}
            totalPages={templateJson.pages.length}
            onChangePage={scrollToPage}
            onChangeZoom={setZoom}
            handleOpenNotes={handleOpenNotes}
          />
        </Box>

        <SidebarButton
          handleToggleSidebar={handleToggleSidebar}
          setSidebarSection={setSidebarSection}
          openBlocksBar={isSidebarOpen}
          sidebarSection={sidebarSection}
        />
        {isSidebarOpen && <ContentSidebar sidebarSection={sidebarSection} />}
        {isVersionHistorySidebarOpen && (
          <VersionHistorySidebar
            documentId={data?.data.id}
            isLoading={isLoading}
            versions={data?.data.versions}
            isTemplate={isTemplate}
          />
        )}
      </Box>
    </Box>
  );

  const renderSection = () => {
    if (isLoading) {
      return renderLoader();
    }

    if (error && 'status' in error && error.status === 404) {
      return renderNotFound();
    }

    return renderDocumentBuilder();
  };

  return <Fragment>{renderSection()}</Fragment>;
};

export default BuilderLayout;

